import { PropsWithChildren } from 'react';
import { makeStyles, Typography } from '@material-ui/core';
import HomeIcon from '@material-ui/icons/Home';
import CategoryIcon from '@material-ui/icons/Category';
import LibraryBooks from '@material-ui/icons/LibraryBooks';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import CodeIcon from '@material-ui/icons/Code';
import SecurityIcon from '@material-ui/icons/Security';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import TimelineIcon from '@material-ui/icons/Timeline';
import { LogoFull } from './LogoFull';
import { LogoIcon } from './LogoIcon';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import { SidebarSearchModal } from '@backstage/plugin-search';
import {
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarScrollWrapper,
  SidebarSpace,
  useSidebarOpenState,
  Link,
} from '@backstage/core-components';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import { MyGroupsSidebarItem } from '@backstage/plugin-org';
import GroupIcon from '@material-ui/icons/People';
import SvgIcon from '@material-ui/core/SvgIcon';
import {
  useProjectRole,
  useSkillVaultEnabled,
  CLIENT_PERMISSION_MATRIX,
} from '@internal/plugin-spassu-code';

const ProjectRolesIcon = (props: React.ComponentProps<typeof SvgIcon>) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <circle
      cx="9"
      cy="7"
      r="3"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    />
    <path
      d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <circle
      cx="19"
      cy="11"
      r="1.8"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
    />
    <path
      d="M19 7.8v1.2M19 12.8v1.2M16.3 9.3l.85.85M20.85 12.85l.85.85M15.8 11h1.2M21 11h1.2M16.3 12.7l.85-.85M20.85 9.15l.85-.85"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.1"
      strokeLinecap="round"
    />
    <path
      d="M16.5 18.5l1.5 1.5 3-3"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </SvgIcon>
);

const useVersionStyles = makeStyles(theme => ({
  version: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    padding: theme.spacing(1, 2),
    textAlign: 'center',
    fontSize: '0.65rem',
    color: theme.palette.text.disabled,
    lineHeight: 1.4,
  },
}));

const SidebarVersion = () => {
  const classes = useVersionStyles();
  const { isOpen } = useSidebarOpenState();

  if (!isOpen) return null;

  return (
    <Typography className={classes.version}>
      Spassu Code v1.0.0
      <br />
      Build: 2871
    </Typography>
  );
};

const useSidebarLogoStyles = makeStyles({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
  },
  link: {
    width: sidebarConfig.drawerWidthClosed,
    marginLeft: 24,
    transition: 'opacity 0.2s ease',
    '&:hover': {
      opacity: 0.85,
    },
  },
});

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <div className={classes.root}>
      <Link to="/" underline="none" className={classes.link} aria-label="Home">
        {isOpen ? <LogoFull /> : <LogoIcon />}
      </Link>
    </div>
  );
};

/**
 * Item "Skill Vault" condicionado à feature flag
 * `spassuCode.features.skillVault` (env `SKILL_VAULT_FEATURE_FLAG`).
 */
export const SkillVaultSidebarItem = () => {
  const enabled = useSkillVaultEnabled();

  if (!enabled) return null;

  return (
    <SidebarItem
      icon={SecurityIcon}
      to="spassu-code/skills"
      text="Skill Vault"
    />
  );
};

const GestaoSidebarItem = () => {
  const { loading, isAdmin, roles } = useProjectRole();

  if (loading) return null;

  const canSee =
    isAdmin ||
    roles.some(p =>
      p.roles.some(
        r =>
          CLIENT_PERMISSION_MATRIX['member.list'].includes(r) ||
          CLIENT_PERMISSION_MATRIX['member.add'].includes(r) ||
          CLIENT_PERMISSION_MATRIX['audit.view'].includes(r),
      ),
    );

  if (!canSee) return null;

  return (
    <SidebarItem
      icon={ProjectRolesIcon}
      to="spassu-code/project-members"
      text="Controle de Acesso"
    />
  );
};

const SettingsSidebarGroup = () => (
  <SidebarGroup
    label="Settings"
    icon={<UserSettingsSignInAvatar />}
    to="/settings"
  >
    <SidebarSettings />
  </SidebarGroup>
);

export const Root = ({ children }: PropsWithChildren<{}>) => (
  <SidebarPage>
    <Sidebar>
      <SidebarLogo />
      <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
        <SidebarSearchModal />
      </SidebarGroup>
      <SidebarDivider />
      <SidebarGroup label="Menu" icon={<MenuIcon />}>
        {/* Global nav, not org-specific */}
        <SidebarItem icon={HomeIcon} to="home" text="Home" />
        <SidebarItem icon={CategoryIcon} to="catalog" text="Catálogo" />
        <MyGroupsSidebarItem
          singularTitle="Grupo"
          pluralTitle="Grupos"
          icon={GroupIcon}
        />
        <SidebarItem icon={LibraryBooks} to="docs" text="Documentação" />
        {/* End global nav */}
        <SidebarDivider />
        <SidebarScrollWrapper>
          {/* Items in this group will be scrollable if they run out of space */}
          <SidebarItem
            icon={CreateComponentIcon}
            to="create"
            text="Templates"
          />
          <SidebarItem icon={CodeIcon} to="spassu-code" text="Frontier" />
          <SkillVaultSidebarItem />
          <SidebarItem icon={AccountTreeIcon} to="orquestra" text="Orquestra" />
          <SidebarItem
            icon={TimelineIcon}
            to="gestao-fluxo"
            text="Gestão de fluxo"
          />
          <GestaoSidebarItem />
        </SidebarScrollWrapper>
      </SidebarGroup>
      <SidebarSpace />
      <SidebarDivider />
      <SettingsSidebarGroup />
      <SidebarVersion />
    </Sidebar>
    {children}
  </SidebarPage>
);
